/**
 * 67.70 (DECISIONS 209 (2), the owner: "would be nice to get a circular CEP radius when calling in the airstrike ... and a
 * landing zone box roughly the dimensions of the target box, or a larger box / circle encompassing the possible landing zone"):
 * what a side's call covers on the ground — while it is aimed (the radial's or the ring's Mark ▸ Airstrike / Dropship here /
 * Drop an APC / Drop pods, or layout C's card armed), and once called until it comes down. The shapes are the sim's own
 * numbers, read and never written:
 *   - **an airstrike:** its rounds fall within the fix's scatter (core/airstrike.ts `airstrikeFix`, the side's picture
 *     there: seen, a picture, a guess) plus the rounds' circle (`radiusPeople`) round the point — a filled circle; its bursts
 *     reach one burst's radius past that (a dashed one);
 *   - **a dropship:** the landing the sim would pick now (core/dropship.ts `callDropship`: the point, or the nearest clear
 *     spot) as its load's box, facing along its track (the container's footprint; an APC's own), and a dashed circle round
 *     the point for where it may move to (the room search's `searchPeople`, plus its room);
 *   - **pods:** the ring they come down in (core/beacon.ts `beaconArrival`: `BEACON.ring` to two people further out).
 * A call that would be refused says why (no calls left, nobody waiting, no room). Only the side's own (fog).
 * Pure: shapes in people round surface directions; `rendering/callMarks.ts` drapes them on the ground.
 */
import type { GameState, Vec3 } from '../types';
import { AIRSTRIKE, BEACON, DROPSHIP, DROPSHIP_ANCHORS, PHYS_DT, PODS, VEHICLE_MODEL_SIZE } from '../constants';
import { EXPLOSIONS } from '../constants/explosions';
import { vecNormalize } from '../math';
import { airstrikeFix } from '../core/airstrike';
import { boardingOpen, callDropship, shipTakeoff, boardingSortie } from '../core/dropship';
import { verbInputs, type Here } from '../core/orderMenu';

/** The calls that get a mark (the Mark verb's subs). */
export type CallSub = 'airstrike' | 'dropship' | 'dropshipVehicle' | 'podDrop';
export const CALL_SUBS: readonly CallSub[] = ['airstrike', 'dropship', 'dropshipVehicle', 'podDrop'];
export const isCallSub = (sub: string | null | undefined): sub is CallSub => !!sub && (CALL_SUBS as readonly string[]).includes(sub);

/** A circle on the ground: its centre, radius (people) and look (`fill`: a faint disc under a solid edge). */
export interface CallCircle { at: Vec3; people: number; style: 'fill' | 'solid' | 'dashed' }
/** A box on the ground: its centre, the way its length runs (a surface tangent at `at`) and its length and width (people). */
export interface CallBox { at: Vec3; facing: Vec3; length: number; width: number }
export interface CallShape {
  sub: CallSub;
  planetIndex: number;
  circles: CallCircle[];
  box?: CallBox;
  /** What the mark says (the fix, the radius; the payload). */
  label: string;
  /** Why the call would be refused now (drawn as a warning, nothing else). */
  refused?: string;
}

/** A person's height in the model units VEHICLE_MODEL_SIZE is in. */
const MODEL_PERSON = 1.8;
const C = DROPSHIP_ANCHORS.container;
/** The troop container's footprint (people): its length along the ship, its width across. */
export const CONTAINER_BOX = { length: C.max[2] - C.min[2], width: C.max[0] - C.min[0] };
/** The APC's footprint (people). */
export const APC_BOX = { length: VEHICLE_MODEL_SIZE.wheeled.length / MODEL_PERSON, width: VEHICLE_MODEL_SIZE.wheeled.width / MODEL_PERSON };
/** The pods' ring (people): the farthest a pod comes down from the point (`beaconArrival`'s third ring) and a pod's half. */
export const POD_RING = BEACON.ring + 2 + 0.5;

/** An airstrike's circles at `at`: its rounds' extent (the fix's scatter and the rounds' circle) and its bursts' reach. */
export function strikeCircles(state: Pick<GameState, 'config'>, at: Vec3, scatterPeople: number, radiusPeople?: number): { circles: CallCircle[]; fall: number; reach: number } {
  const fall = scatterPeople + (radiusPeople ?? state.config.airstrikeRadius ?? AIRSTRIKE.radiusPeople);
  const reach = fall + (EXPLOSIONS[AIRSTRIKE.explosion].blast?.radiusPeople ?? 0);
  return { circles: [{ at, people: fall, style: 'fill' }, { at, people: reach, style: 'dashed' }], fall, reach };
}

const FIX_WORDS = { seen: 'seen', picture: 'from the picture', guess: 'a guess' } as const;

/**
 * The call `sub` aimed at `here` by `team` now: its shape, or its refusal (null with no ground under the cursor). Calls the
 * sim's own pure functions (verbInputs for the refusal, airstrikeFix, callDropship) and writes nothing; the host asks again
 * only when the point moves.
 */
export function previewCall(state: GameState, team: number, sub: CallSub, here: Here | null, tick: number, speaker: number | null = null): CallShape | null {
  if (!here) return null;
  const at = vecNormalize(here.surfaceDir), planetIndex = here.planetIndex;
  const r = verbInputs(state, team, [], 'mark', sub, here, speaker);
  if ('refused' in r) return { sub, planetIndex, circles: [{ at, people: 1.5, style: 'solid' }], label: r.refused, refused: r.refused };
  if (sub === 'airstrike') {
    const { fix, scatterPeople } = airstrikeFix(state, team, planetIndex, at, tick);
    const s = strikeCircles(state, at, scatterPeople);
    return { sub, planetIndex, circles: s.circles, label: `${FIX_WORDS[fix]} · rounds within ${Math.round(s.fall)} · in ${Math.round(AIRSTRIKE.prepSeconds)} s` };
  }
  if (sub === 'podDrop') return { sub, planetIndex, circles: [{ at, people: POD_RING, style: 'fill' }], label: `pods in ${Math.round(PODS.prepSeconds)} s` };
  const vehicle = sub === 'dropshipVehicle';
  const c = callDropship(state, team, { planetIndex, surfaceDir: at }, tick, vehicle ? { payload: 'vehicle', vehicle: 'wheeled' } : { payload: 'troops' });
  if ('refused' in c) return { sub, planetIndex, circles: [{ at, people: DROPSHIP.clearPeople, style: 'solid' }], label: c.refused, refused: c.refused };
  const f = c.flight, room = DROPSHIP.moveBlocked ? DROPSHIP.searchPeople + DROPSHIP.clearPeople : DROPSHIP.clearPeople;
  return {
    sub, planetIndex, circles: [{ at, people: room, style: 'dashed' }], box: { at: f.land, facing: f.facing, ...(vehicle ? APC_BOX : CONTAINER_BOX) },
    label: `${vehicle ? 'APC' : 'dropship'} · lands in ${Math.round((f.landTick - tick) * PHYS_DT)} s`,
  };
}

/** A call standing (made, not yet down): its shape, a key to keep its mark by, and the seconds till it comes down. */
export interface StandingCall extends CallShape { key: string; seconds: number }

/**
 * The side's calls made and not yet down, read from the state: each airstrike till its last round lands (the countdown to
 * its first), each called dropship till its load is on the ground (a wave's sortie is no call), each pod drop till it lands.
 */
export function standingCalls(state: Pick<GameState, 'airstrikes' | 'dropships' | 'pods' | 'config'>, team: number, tick: number): StandingCall[] {
  const out: StandingCall[] = [];
  const secs = (t: number) => Math.max(0, (t - tick) * PHYS_DT);
  for (const s of state.airstrikes?.inbound ?? []) {
    if (s.team !== team || tick > s.impactTick) continue;
    const first = s.rounds.length ? Math.min(...s.rounds.map(q => q.landTick)) : s.impactTick;
    const c = strikeCircles(state, s.called, s.scatterPeople + (s.driftPeople ?? 0), s.radiusPeople);   // (the wind correction's uncertainty taken in)
    out.push({ key: `strike:${s.id}`, sub: 'airstrike', planetIndex: s.planetIndex, circles: c.circles, label: `${s.kind === 'mortar' ? 'mortar' : 'airstrike'} · ${FIX_WORDS[s.fix]}`, seconds: secs(first) });
  }
  for (const f of state.dropships?.flights ?? []) {
    const down = f.payload === 'vehicle' ? f.exitTick : f.landTick;
    if (f.team !== team || f.sortie || f.piloted || f.rejoin || tick >= down) continue;   // (69.17 step 3: a flown ship, or one on its way back to its routine, is no call)
    const vehicle = f.payload === 'vehicle';
    out.push({ key: `ship:${f.id}`, sub: vehicle ? 'dropshipVehicle' : 'dropship', planetIndex: f.planetIndex, circles: [], box: { at: f.land, facing: f.facing, ...(vehicle ? APC_BOX : CONTAINER_BOX) }, label: vehicle ? 'APC' : 'dropship', seconds: secs(down) });
  }
  for (const d of state.pods?.drops ?? []) {
    if (d.team !== team || tick >= d.dropTick) continue;
    out.push({ key: `pods:${d.id}`, sub: 'podDrop', planetIndex: d.planetIndex, circles: [{ at: d.at, people: POD_RING, style: 'fill' }], label: `pods · ${d.units.length}`, seconds: secs(d.dropTick) });
  }
  return out;
}

/** DECISIONS 209 (5): what a wave ship's destination is called — its structure's kind (a FOB, a relay, the beacon), or the push's landing. */
export function shipDestLabel(state: Pick<GameState, 'structures'>, at: number): string {
  if (at < 0) return 'the landing';
  const s = state.structures.find(x => x.id === at);
  return s?.type === 'fob' ? 'the FOB' : s?.type === 'relay' ? 'the relay' : s?.type === 'beacon' ? 'the beacon' : 'the front';
}

/**
 * DECISIONS 209 (5) (the coordinator: "a marker on the ship"): the side's wave ship on its pad while it takes riders — its box
 * where it stands, its destination, and its departure (the seconds left once one is aboard; the full wait before).
 */
export function waveShipCall(state: GameState, team: number, tick: number): StandingCall | null {
  const pad = state.shipPads?.[team];
  if (!pad?.dest || !boardingOpen(state, team)) return null;
  const q = boardingSortie(state, team), first = q?.units.find(id => state.players.find(p => p.id === id)?.boarded);
  const seconds = first !== undefined ? shipTakeoff(state, first, tick) ?? DROPSHIP.sortieSeconds : DROPSHIP.sortieSeconds;
  return { key: `wave:${team}`, sub: 'dropship', planetIndex: pad.planetIndex, circles: [], box: { at: pad.dir, facing: pad.facing, ...CONTAINER_BOX }, label: `dropship → ${shipDestLabel(state, pad.dest.at)}`, seconds };
}

